import { useState, useEffect } from 'react';
import { createFilterOptions, isValidId, updateActiveTask, Util } from '../../util/Util';
import RefreshButton from '../../components/RefreshButton';
import { useOffer } from './OfferContext';
import { useUI } from '../../providers/MoleUIContext';
import { useAppSocket } from '../../providers/AppSocketContext';
import { MIN_DATE, MYSQL_LIMITS } from '../../util/constants';
import { useAuth } from '../../hooks/useAuth';
import { Autocomplete, Grid, MenuItem, TextField } from '@mui/material';
import AddressLineButton from '../client/AddressLineButton';
import { detachFromBook } from '../client/contactBlock';
import AddressLineDialog from '../client/AddressLineDialog';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from "../../components/MoleDatePicker";
import { OFFER_KINDS, offerKindI18nKey, type OfferKind } from './offerTypes';
import { businessTodayDate, parseISODay } from "../../util/businessClock";

interface Props {
	setValidateCheck: (value: boolean) => void;
	locked?: boolean;
}

type ClientOption = { id: number; acc_name: string; is_agent?: boolean };

const asDate = (value: unknown): Date | null => {
	if (!(value instanceof Date) || Number.isNaN(value.getTime())) return null;
	return value;
};

const OfferDetail = ({ setValidateCheck, locked = false }: Props) => {
	const [clients, setClients] = useState<ClientOption[]>([]);
	const [addressOpen, setAddressOpen] = useState(false);
	const [createdClientId, setCreatedClientId] = useState<number | null>(null);
	const [isClientLoading, setIsClientLoading] = useState(false);
	const [errorInput, setErrorInput] = useState('');
	const { setPendingTasks, activeTask } = useOffer();
	const { appSettings } = useAppSocket();
	const { queueReRun, t } = useUI();
	const { api } = useAuth();
	const minDate = appSettings?.SYSTEM_INCEPTION_DATE?.value || MIN_DATE;
	const showAgents = Util.isBoolSettingEnabled(appSettings?.UI_ENABLE_AGENTS?.value);
	const disabled = !activeTask || locked;

	const fetchClients = async (selectId?: number) => {
		setIsClientLoading(true);
		const res = await api.get('/clients', { params: { Active: true, Type: 'CONSUMER' } });
		if (res?.status === 200) {
			const rows = Array.isArray(res.data) ? res.data : [];
			rows.sort((a: ClientOption, b: ClientOption) => a.acc_name.localeCompare(b.acc_name));
			setClients(rows);
			if (selectId && rows.some((client: ClientOption) => client.id === selectId)) {
				updateActiveTask(setPendingTasks, { receiver: selectId });
			}
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(fetchClients);
		}
		setIsClientLoading(false);
	};

	const onKindChanged = (kind: OfferKind) => {
		updateActiveTask(setPendingTasks, { kind });
	};

	const onReceiverChanged = (receiver: number) => {
		// A saved address belongs to the client it was saved under, so switching
		// client keeps what was typed but drops the link to that entry.
		const changed = receiver !== activeTask?.receiver;
		updateActiveTask(setPendingTasks, {
			receiver,
			...(changed && activeTask?.contact ? { contact: detachFromBook(activeTask.contact) } : {}),
		});
	};

	const onAgentChanged = (agent: number) => {
		updateActiveTask(setPendingTasks, { agent });
	};

	const onRefChanged = (reference: string = Util.getDocumentNumber()) => {
		updateActiveTask(setPendingTasks, { reference });
	};

	const onDateChanged = (taskDate: Date = businessTodayDate()) => {
		updateActiveTask(setPendingTasks, { date: Util.formatISODate(taskDate) });
	};

	const onValidUntilChanged = (dueDate: Date | null) => {
		if (!dueDate || Util.compareStrings(dueDate.toString(), 'Invalid Date')) {
			updateActiveTask(setPendingTasks, { valid_until: null });
			return;
		}
		updateActiveTask(setPendingTasks, { valid_until: Util.formatISODate(dueDate) });
	};

	useEffect(() => {
		void fetchClients();
	}, []);

	useEffect(() => {
		setValidateCheck(isValidId(clients, activeTask?.receiver));
	}, [activeTask, clients]);

	return (
		<Grid container spacing={0}>
			<Grid size={{ xs: 6, sm: 4, md: 2 }}>
				<TextField
					select
					id="kindInput"
					size="small"
					label={t('common:offer.kind')}
					variant="filled"
					fullWidth
					value={activeTask?.kind || OFFER_KINDS[0]}
					onChange={(event) => onKindChanged(event.target.value as OfferKind)}
					disabled={disabled}
				>
					{OFFER_KINDS.map((value) => (
						<MenuItem key={value} value={value}>{t(offerKindI18nKey(value))}</MenuItem>
					))}
				</TextField>
			</Grid>
			<Grid size={{ xs: 6, sm: 4, md: 2 }}>
				<Autocomplete
					id="clientInput"
					size="small"
					options={clients}
					filterOptions={createFilterOptions((opt) => opt.acc_name || '')}
					getOptionLabel={(client) => client.acc_name || ''}
					value={clients.find((c) => c.id === activeTask?.receiver) || null}
					onChange={(_, newValue) => onReceiverChanged(newValue?.id || -1)}
					loading={isClientLoading}
					autoHighlight
					autoComplete
					autoSelect
					disabled={disabled || isClientLoading}
					renderInput={(params) => (
						<TextField
							{...params}
							label={
								<span>
									{t('common:tables.client')}&nbsp;&nbsp;
									<RefreshButton isWorking={isClientLoading} onClick={fetchClients} disabled={isClientLoading} />
									<AddressLineButton
										contact={activeTask?.contact}
										disabled={disabled}
										onClick={() => setAddressOpen(true)}
									/>
								</span>
							}
							variant="filled"
							fullWidth
						/>
					)}
				/>
			</Grid>
			{showAgents && (
				<Grid size={{ xs: 6, sm: 4, md: 2 }}>
					<Autocomplete
						id="agentInput"
						size="small"
						options={clients.filter((row) => row.is_agent)}
						filterOptions={createFilterOptions((opt) => opt.acc_name || '')}
						getOptionLabel={(client) => client.acc_name || ''}
						value={clients.find((c) => c.id === activeTask?.agent) || null}
						onChange={(_, newValue) => onAgentChanged(newValue?.id || -1)}
						loading={isClientLoading}
						autoHighlight
						autoComplete
						autoSelect
						disabled={disabled || isClientLoading}
						renderInput={(params) => (
							<TextField
								{...params}
								label={t('common:tables.agent')}
								variant="filled"
								fullWidth
							/>
						)}
					/>
				</Grid>
			)}
			<Grid size={{ xs: 6, sm: 4, md: 2 }}>
				<TextField
					id="referenceInput"
					size="small"
					label={
						<span>
							{t('common:supplyDetail.reference')}&nbsp;&nbsp;
							<RefreshButton isWorking={false} onClick={() => onRefChanged()} disabled={disabled} />
						</span>
					}
					variant="filled"
					fullWidth
					type="text"
					value={activeTask?.reference || ''}
					inputProps={{ maxLength: MYSQL_LIMITS.VARCHAR(50).maxLength }}
					onBlur={(e) => {
						if (!e.target.value || e.target.value.trim() === '') onRefChanged();
					}}
					onChange={(e) => onRefChanged(e.target.value.trim())}
					disabled={disabled}
				/>
			</Grid>
			<Grid size={{ xs: 6, sm: 4, md: 2 }}>
				<LocalizationProvider dateAdapter={AdapterDateFns}>
					<DatePicker
						label={
							<span>
								{t('common:ui.labels.date')}&nbsp;&nbsp;
								<RefreshButton isWorking={false} onClick={() => onDateChanged()} disabled={disabled} />
							</span>
						}
						value={activeTask?.date ? parseISODay(activeTask.date) as Date : businessTodayDate()}
						minDate={(parseISODay(minDate) as Date)}
						onChange={(newDate) => {
							const date = asDate(newDate);
							if (!date || date < (parseISODay(minDate) as Date)) {
								setErrorInput('dateInput');
								setTimeout(() => setErrorInput(''), 1500);
								return;
							}
							onDateChanged(date);
						}}
						disabled={disabled}
						slotProps={{
							textField: {
								id: 'dateInput',
								size: 'small',
								fullWidth: true,
								variant: 'filled',
								error: errorInput === 'dateInput',
								helperText: errorInput === 'dateInput' ? t('common:validation.invalidDate') : '',
							},
						}}
					/>
				</LocalizationProvider>
			</Grid>
			<Grid size={{ xs: 6, sm: 4, md: 2 }}>
				<LocalizationProvider dateAdapter={AdapterDateFns}>
					<DatePicker
						label={t('common:offer.validUntil')}
						value={activeTask?.valid_until ? parseISODay(activeTask.valid_until) : null}
						minDate={activeTask?.date ? parseISODay(activeTask.date) as Date : (parseISODay(minDate) as Date)}
						onChange={(newDate) => {
							onValidUntilChanged(asDate(newDate));
						}}
						disabled={disabled}
						slotProps={{
							textField: {
								size: 'small',
								fullWidth: true,
								variant: 'filled',
							},
						}}
					/>
				</LocalizationProvider>
			</Grid>
			<AddressLineDialog
				open={addressOpen}
				onClose={() => {
					setAddressOpen(false);
					// A client created from here is only in the local list; reload
					// it from the server now that the dialog is out of the way.
					if (createdClientId) {
						fetchClients(createdClientId);
						setCreatedClientId(null);
					}
				}}
				clientId={activeTask?.receiver}
				value={activeTask?.contact}
				existingNames={clients.map((client) => client.acc_name)}
				onApply={(contact) => updateActiveTask(setPendingTasks, { contact })}
				onClientCreated={(client) => {
					setClients((prev) => [...prev, client as ClientOption].sort((a, b) => a.acc_name.localeCompare(b.acc_name)));
					updateActiveTask(setPendingTasks, { receiver: client.id });
					setCreatedClientId(client.id);
				}}
			/>
		</Grid>
	);
};

export default OfferDetail;
